import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import CategoryFilter from "@/components/CategoryFilter";
import LanguagePills from "@/components/LanguagePills";
import FeaturedAdsCarousel from "@/components/FeaturedAdsCarousel";
import { MovieCard, EventCard } from "@/components/Cards";
import SectionHeader from "@/components/SectionHeader";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Instagram, Radio, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const DEFAULT_SEASONAL_CAMPAIGN = {
  enabled: true,
  label: "Navratri 2026",
  title: "Garba, Dandiya and Raas nights",
  subtitle: "Find verified UK Navratri events, tickets and external listings.",
  route: "/navratri",
};

const DEFAULT_CREATOR_SHOWCASE = {
  enabled: true,
  eyebrow: "Community voices",
  title: "Featured creators",
  subtitle: "Influencers, food bloggers, promoters and community pages recommending UK Indian events.",
  limit: 8,
};

export default function Home() {
  const { city, language, category, categories, campaigns } = useApp();
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventbrite, setEventbrite] = useState([]);
  const [creators, setCreators] = useState([]);
  const [seasonalCampaign, setSeasonalCampaign] = useState(DEFAULT_SEASONAL_CAMPAIGN);
  const [creatorShowcase, setCreatorShowcase] = useState(DEFAULT_CREATOR_SHOWCASE);

  useEffect(() => {
    const params = (extra = {}, options = {}) => {
      const p = new URLSearchParams();
      if (language && language !== "All") p.set("language", language);
      if (city) p.set("city", city);
      if (options.includeCategory !== false && category && category !== "All") p.set("category", category);
      Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
      return p.toString();
    };
    const moviesEnabled = (categories || []).some((item) => item.name === "Movies");
    const showMovies = moviesEnabled && (!category || category === "All" || category === "Movies");
    const showEvents = !category || category === "All" || category !== "Movies";

    if (showMovies) {
      api.get(`/movies?${params({}, { includeCategory: false })}`).then((r) => setMovies(r.data || []));
    } else {
      setMovies([]);
    }

    if (showEvents) {
      api.get(`/events?${params()}`).then((r) => setEvents(r.data || []));
      api.get(`/eventbrite/events?${params()}`).then((r) => setEventbrite(r.data || []));
    } else {
      setEvents([]);
      setEventbrite([]);
    }

    api.get("/creators", {
      params: {
        city: city || undefined,
        language: language && language !== "All" ? language : undefined,
      },
    }).then((r) => setCreators(r.data || [])).catch(() => setCreators([]));
    api.get("/app-options/seasonal_campaign")
      .then((r) => setSeasonalCampaign({ ...DEFAULT_SEASONAL_CAMPAIGN, ...(r.data?.value || {}) }))
      .catch(() => setSeasonalCampaign(DEFAULT_SEASONAL_CAMPAIGN));
    api.get("/app-options/creator_showcase")
      .then((r) => setCreatorShowcase({ ...DEFAULT_CREATOR_SHOWCASE, ...(r.data?.value || {}) }))
      .catch(() => setCreatorShowcase(DEFAULT_CREATOR_SHOWCASE));
  }, [city, language, category, categories]);

  const moviesEnabled = (categories || []).some((item) => item.name === "Movies");

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,hsl(var(--secondary)/0.12),transparent_55%)]" />
        <div className="relative page-shell py-12 md:py-16">
          <p className="text-[11px] md:text-xs uppercase tracking-[0.4em] font-black text-primary">Tickets · UK Indian Community</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-3 max-w-3xl">
            Your night out, <span className="text-primary">curated</span>.<br />
            {moviesEnabled ? "Movies, Garba, Comedy & more" : "Garba, Comedy, Food & more"} — across the UK.
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl">
            Live Eventbrite picks · regional language premieres · seat-by-seat booking · QR at the door.
          </p>
          <div className="mt-7">
            <LanguagePills />
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            {seasonalCampaign.enabled !== false && (
              <Button onClick={() => navigate(seasonalCampaign.route || "/navratri")} className="rounded-full" data-testid="home-seasonal-cta">
                <Sparkles className="mr-2 h-4 w-4" /> {seasonalCampaign.title || seasonalCampaign.label}
              </Button>
            )}
            <Button onClick={() => navigate("/create-event")} variant="outline" className="rounded-full">
              List your event
            </Button>
          </div>
        </div>
      </section>

      <main className="page-shell py-10 space-y-14">
        {/* Categories */}
        <section data-testid="home-categories">
          <SectionHeader eyebrow="Browse" title="What's your vibe?" />
          <CategoryFilter />
        </section>

        {campaigns?.some((campaign) => campaign.route) && (
          <section data-testid="home-campaigns">
            <SectionHeader eyebrow="Campaigns" title="Festival and culture hubs" />
            <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
              {campaigns.filter((campaign) => campaign.route).map((campaign) => (
                <button
                  key={campaign.key}
                  onClick={() => navigate(campaign.route)}
                  className="group min-w-[260px] rounded-2xl border border-border bg-card p-5 text-left transition hover:-translate-y-0.5 hover:border-primary/50"
                >
                  <div className="flex items-center justify-between gap-3">
                    <Badge className="bg-primary text-primary-foreground">
                      <Sparkles className="mr-1 h-3.5 w-3.5" />
                      {campaign.label}
                    </Badge>
                    <span className="text-lg font-black text-primary transition group-hover:translate-x-1">→</span>
                  </div>
                  <p className="mt-4 font-display text-xl font-black">{campaign.label}</p>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{campaign.description || "Explore curated events, featured picks and promoted listings."}</p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Featured ads */}
        <FeaturedAdsCarousel language={language} />

        {/* Creators */}
        {creatorShowcase.enabled !== false && (
          <section>
            <SectionHeader
              eyebrow={creatorShowcase.eyebrow || DEFAULT_CREATOR_SHOWCASE.eyebrow}
              title={creatorShowcase.title || DEFAULT_CREATOR_SHOWCASE.title}
              action={
                <Button variant="ghost" className="rounded-full" onClick={() => navigate("/creators")}>
                  Explore creators
                </Button>
              }
            />
            {creatorShowcase.subtitle && (
              <p className="-mt-2 mb-5 max-w-2xl text-sm text-muted-foreground">{creatorShowcase.subtitle}</p>
            )}
            {creators.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                Creator profiles are opening soon for influencers, food bloggers, organisers and community pages.
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2" data-testid="featured-creators-rail">
                {creators.slice(0, Number(creatorShowcase.limit || 8)).map((creator) => <FeaturedCreatorCard key={creator.user_id} creator={creator} />)}
              </div>
            )}
          </section>
        )}

        {/* Eventbrite Live */}
        <section>
          <SectionHeader
            eyebrow="Live on Eventbrite"
            title="Trending UK Indian events"
            action={
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-destructive">
                <Radio className="h-3.5 w-3.5" /> {eventbrite.length} LIVE NOW
              </span>
            }
          />
          <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2" data-testid="eventbrite-rail">
            {eventbrite.slice(0, 12).map((e) => (
              <EventCard key={e.eventbrite_id} event={e} isLive />
            ))}
          </div>
        </section>

        {moviesEnabled && (
          <section>
            <SectionHeader eyebrow="Now showing" title="Movies near you" />
            {movies.length === 0 ? (
              <p className="text-muted-foreground text-sm">No movies for this filter. Try a different language or city.</p>
            ) : (
              <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2" data-testid="movies-rail">
                {movies.map((m) => <MovieCard key={m.movie_id} movie={m} />)}
              </div>
            )}
          </section>
        )}

        {/* Internal Events */}
        <section>
          <SectionHeader eyebrow="Don't miss" title="Curated experiences" />
          {events.length === 0 ? (
            <p className="text-muted-foreground text-sm">No events match your filter yet.</p>
          ) : (
            <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2" data-testid="events-rail">
              {events.map((e) => <EventCard key={e.event_id} event={e} />)}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-border mt-10">
        <div className="page-shell py-8 text-xs text-muted-foreground flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span>© 2026 NamasteNetwork.ai · Built with love for the UK Indian community.</span>
          <nav className="flex flex-wrap items-center gap-4" aria-label="Help links">
            <a href="/faq" className="font-bold hover:text-primary">FAQ</a>
            <a href="/organiser-guide" className="font-bold hover:text-primary">Organiser guide</a>
            <a href="/terms" className="font-bold hover:text-primary">Terms</a>
            <a href="/privacy" className="font-bold hover:text-primary">Privacy</a>
            <span className="hidden md:inline">Made in London · Birmingham · Leicester</span>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function FeaturedCreatorCard({ creator }) {
  const navigate = useNavigate();
  const name = creator.display_name || creator.name || "Namaste creator";
  const locations = creator.creator_locations || creator.city_preferences || [];
  const categories = creator.creator_categories || [];
  return (
    <article className="flex-shrink-0 w-72 rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary/50">
      <button type="button" onClick={() => navigate(`/creators/${creator.user_id}`)} className="w-full text-left">
      <div className="flex items-start gap-3">
        <Avatar className="h-14 w-14 border border-border">
          <AvatarImage src={creator.profile_image_url || creator.picture} />
          <AvatarFallback>{name.slice(0, 1)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {creator.featured_creator && <Sparkles className="h-4 w-4 text-primary" />}
            <h3 className="font-display text-lg font-black leading-tight line-clamp-1">{name}</h3>
          </div>
          {creator.instagram_handle && <p className="text-sm font-bold text-primary line-clamp-1">{creator.instagram_handle}</p>}
          <p className="mt-1 text-xs text-muted-foreground line-clamp-1">{locations.slice(0, 2).join(", ") || "UK"}</p>
        </div>
      </div>
      </button>
      {categories.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {categories.slice(0, 3).map((item) => <Badge key={item} variant="secondary">{item}</Badge>)}
        </div>
      )}
      {creator.bio && <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{creator.bio}</p>}
      {creator.instagram_url && (
        <Button size="sm" variant="outline" asChild className="mt-4 rounded-full">
          <a href={creator.instagram_url} target="_blank" rel="noreferrer"><Instagram className="mr-2 h-4 w-4" />Connect</a>
        </Button>
      )}
      <Button size="sm" className="mt-4 ml-2 rounded-full" onClick={() => navigate(`/creators/${creator.user_id}`)}>
        View profile
      </Button>
    </article>
  );
}
